import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Coffee, GripVertical, Upload, Image as ImageIcon, X, Music, Play } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, TouchSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/NumberInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';
import { DEFAULT_BLIND_STRUCTURE } from '../mock';

// Пересчитываем номера уровней: перерывы не нумеруются, обычные уровни идут 1, 2, 3, ...
function renumber(blinds) {
  let n = 0;
  return blinds.map((b) => {
    if (b.isBreak) return { ...b, level: 0 };
    n += 1;
    return { ...b, level: n };
  });
}

function SortableRow({ id, index, b, updateBlind, removeBlind }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#fee2e2' : undefined
  };
  return (
    <tr ref={setNodeRef} style={style} className={`border-b border-neutral-100 ${b.isBreak ? 'bg-amber-50/60' : ''}`}>
      <td className="py-2 pr-2 w-8">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="flex h-8 w-6 cursor-grab touch-none items-center justify-center text-neutral-400 hover:text-neutral-900 active:cursor-grabbing"
          aria-label="Перетащить"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="py-2 pr-2 font-semibold w-20">
        {b.isBreak
          ? <span className="inline-flex items-center gap-1 text-amber-700"><Coffee className="h-3 w-3" /> Перерыв</span>
          : b.level}
      </td>
      {b.isBreak ? (
        <td className="py-2 pr-2" colSpan={3}>
          <Input value={b.breakLabel || 'Перерыв'} onChange={(e) => updateBlind(index, { breakLabel: e.target.value })} className="h-8" />
        </td>
      ) : (
        <>
          <td className="py-2 pr-2"><NumberInput value={b.sb} onChange={(v) => updateBlind(index, { sb: v })} className="h-8 w-24" /></td>
          <td className="py-2 pr-2"><NumberInput value={b.bb} onChange={(v) => updateBlind(index, { bb: v })} className="h-8 w-24" /></td>
          <td className="py-2 pr-2"><NumberInput value={b.ante} onChange={(v) => updateBlind(index, { ante: v })} className="h-8 w-20" /></td>
        </>
      )}
      <td className="py-2 pr-2"><NumberInput value={b.duration} onChange={(v) => updateBlind(index, { duration: v })} className="h-8 w-16" /></td>
      <td className="py-2 pr-2 text-right">
        <button
          type="button"
          onClick={() => removeBlind(index)}
          className="text-neutral-400 hover:text-red-600"
          title="Удалить"
        ><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

function SoundSlotCard({ slot, index, onChange, onRemove }) {
  const fileRef = React.useRef(null);
  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      onChange({ error: 'Выберите аудиофайл (MP3, WAV, OGG)' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      onChange({ error: 'Файл больше 2 МБ. Сожмите или укоротите.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange({ patch: { name: file.name, dataUrl: reader.result } });
    };
    reader.readAsDataURL(file);
  };
  const play = () => {
    if (!slot?.dataUrl) return;
    try {
      const a = new Audio(slot.dataUrl);
      a.play().catch(() => {});
    } catch (e) { /* noop */ }
  };
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-red-600">Звук #{index + 1}</div>
        {slot?.dataUrl && (
          <button type="button" onClick={() => onChange({ clear: true })} className="text-neutral-400 hover:text-red-600" title="Очистить">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="audio/*" onChange={onPick} className="hidden" />
      {slot?.dataUrl ? (
        <>
          <div className="mt-3 flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
            <Music className="h-4 w-4 text-red-600" />
            <div className="min-w-0 flex-1 truncate text-sm font-medium">{slot.name || 'звук.mp3'}</div>
            <button type="button" onClick={play} className="rounded-md border border-neutral-200 bg-white p-1.5 hover:bg-neutral-100" title="Прослушать">
              <Play className="h-3.5 w-3.5" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 py-2 text-xs text-neutral-700 hover:bg-neutral-100"
          >
            <Upload className="h-3 w-3" /> Заменить файл
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="mt-3 flex w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 py-5 text-sm text-neutral-600 hover:border-red-400 hover:bg-red-50 hover:text-red-600"
        >
          <Upload className="h-5 w-5" />
          <span>Загрузить .mp3 / .wav</span>
          <span className="text-xs text-neutral-400">до 2 МБ</span>
        </button>
      )}
      <div className="mt-3">
        <Label className="text-xs text-neutral-600">За сколько секунд до конца уровня играть</Label>
        <NumberInput
          min={0}
          value={slot?.secondsBefore ?? 0}
          onChange={(v) => onChange({ patch: { secondsBefore: v } })}
          className="mt-1 h-9"
        />
        <div className="mt-1 text-[11px] text-neutral-500">0 — воспроизвести в самом конце уровня</div>
      </div>
    </div>
  );
}

export default function TournamentEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const { user, loading, getById, createTournament, updateTournament, fetchFullTournament } = useApp();
  const { toast } = useToast();

  const existing = id ? getById(id) : null;
  const [name, setName] = React.useState(existing?.name || '');
  const [subtitle, setSubtitle] = React.useState(existing?.subtitle || '');
  const [code, setCode] = React.useState(existing?.code || '');
  const [blinds, setBlinds] = React.useState(() => renumber(existing?.blinds || DEFAULT_BLIND_STRUCTURE));
  const [backgroundImage, setBackgroundImage] = React.useState(existing?.backgroundImage || '');
  const [showAvgStack] = React.useState(existing?.showAvgStack !== false);
  const [sideInfo, setSideInfo] = React.useState(existing?.sideInfo || '');
  const [sounds, setSounds] = React.useState(() => {
    const s = existing?.sounds || [];
    // Всегда 2 слота
    return [0, 1].map((i) => s[i] || { id: `slot-${i}`, name: '', secondsBefore: 0, dataUrl: '' });
  });
  const fileInputRef = React.useRef(null);
  // Флаг «полное состояние догружено» — блокирует кнопку Сохранить, пока
  // не приедет backgroundImage / sounds из GET /tournaments/{id}. Без него
  // ранний Save после открытия редактора отправит на сервер пустой bg и
  // сотрёт существующий фон.
  const [hydrated, setHydrated] = React.useState(!id);

  React.useEffect(() => { if (!loading && !user) nav('/admin/login'); }, [user, loading, nav]);

  // Догружаем ПОЛНОЕ состояние (список из /tournaments отдаёт lite, без
  // тяжёлых base64 blob'ов) и восстанавливаем backgroundImage/sounds для
  // редактирования.
  React.useEffect(() => {
    if (!id) return;
    (async () => {
      const full = await fetchFullTournament(id);
      if (full) {
        if (full.backgroundImage) setBackgroundImage(full.backgroundImage);
        if (Array.isArray(full.sounds) && full.sounds.length) {
          setSounds([0, 1].map((i) => full.sounds[i] || { id: `slot-${i}`, name: '', secondsBefore: 0, dataUrl: '' }));
        }
      }
      setHydrated(true);
    })();
  }, [id]);

  const updateBlind = (idx, patch) => setBlinds((bs) => renumber(bs.map((b, i) => (i === idx ? { ...b, ...patch } : b))));
  const removeBlind = (idx) => setBlinds((bs) => renumber(bs.filter((_, i) => i !== idx)));
  const addBlind = () => {
    const last = blinds.filter((b) => !b.isBreak).slice(-1)[0];
    const sb = last ? Math.round(last.sb * 1.5 / 50) * 50 : 100;
    setBlinds((bs) => renumber([...bs, { level: 0, sb, bb: sb * 2, ante: 0, duration: 20, isBreak: false }]));
  };
  const addBreak = () => setBlinds((bs) => renumber([...bs, { level: 0, sb: 0, bb: 0, ante: 0, duration: 10, isBreak: true, breakLabel: 'Перерыв' }]));

  // dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  // Стабильные id для dnd (по индексу — потому что уровни не имеют своего id)
  const rowIds = blinds.map((_, i) => `row-${i}`);
  const onDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const from = rowIds.indexOf(active.id);
    const to = rowIds.indexOf(over.id);
    if (from === -1 || to === -1) return;
    setBlinds((bs) => renumber(arrayMove(bs, from, to)));
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Не изображение', description: 'Выберите JPG, PNG или WEBP', variant: 'destructive' });
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast({ title: 'Файл слишком большой', description: 'Максимум 4 МБ', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBackgroundImage(reader.result);
    reader.readAsDataURL(file);
  };

  const onSoundChange = (idx) => (payload) => {
    if (payload.error) {
      toast({ title: 'Не подошёл файл', description: payload.error, variant: 'destructive' });
      return;
    }
    setSounds((ss) => ss.map((s, i) => {
      if (i !== idx) return s;
      if (payload.clear) return { id: s.id, name: '', secondsBefore: 0, dataUrl: '' };
      return { ...s, ...payload.patch };
    }));
  };

  const save = async () => {
    if (!name.trim()) { toast({ title: 'Укажите название', description: 'Введите название турнира', variant: 'destructive' }); return; }
    // Проверка кода
    const codeTrim = code.trim();
    if (codeTrim && !/^\d{1,5}$/.test(codeTrim)) {
      toast({ title: 'Некорректный код', description: 'Код должен состоять из 1–5 цифр', variant: 'destructive' });
      return;
    }
    const payload = {
      name: name.trim(),
      subtitle: subtitle.trim(),
      descriptionLines: [],
      blinds,
      backgroundImage,
      showAvgStack,
      sideInfo,
      sounds: sounds.filter((s) => s.dataUrl),
      ...(codeTrim ? { code: codeTrim } : {})
    };
    try {
      if (existing) {
        await updateTournament(existing.id, payload);
        toast({ title: 'Сохранено', description: 'Турнир обновлён' });
        nav(`/admin/tournament/${existing.id}`);
      } else {
        const t = await createTournament(payload);
        toast({ title: 'Создано', description: `Код ${t.code} сгенерирован` });
        nav(`/admin/tournament/${t.id}`);
      }
    } catch (e) {
      toast({ title: 'Ошибка сохранения', description: e.message || e?.response?.data?.detail || 'Попробуйте ещё раз', variant: 'destructive' });
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => nav('/admin')} className="text-neutral-600 hover:text-red-600">
              <ArrowLeft className="mr-2 h-4 w-4" /> К турнирам
            </Button>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">{existing ? 'Редактирование турнира' : 'Новый турнир'}</div>
              <div className="text-lg font-bold">{existing?.name || 'Без названия'}</div>
            </div>
          </div>
          <Button onClick={save} disabled={!hydrated} className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            <Save className="mr-2 h-4 w-4" /> {hydrated ? 'Сохранить' : 'Загрузка…'}
          </Button>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-3">
        <Card className="rounded-2xl lg:col-span-1">
          <CardContent className="space-y-4 p-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-red-600">1 · Основное</div>
            <div>
              <Label>Название турнира</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Пятничный Дипстек" />
            </div>
            <div>
              <Label>Подзаголовок</Label>
              <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Ежемесячный турнир клуба" />
            </div>
            <div>
              <Label>Код турнира (1–5 цифр, опционально)</Label>
              <Input
               
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder={existing ? existing.code : 'Оставьте пустым для автогенерации'}
                inputMode="numeric"
                maxLength={5}
              />
              <div className="mt-1 text-xs text-neutral-500">Если пусто, сервер сгенерирует случайный 2-значный код. Иначе — код должен быть уникальным.</div>
            </div>
            <div>
              <Label>Описание справа на ТВ</Label>
              <Textarea rows={5} value={sideInfo} onChange={(e) => setSideInfo(e.target.value)} placeholder={"Первый турнир месяца!\n\nПобедитель получает главный кубок клуба."} />
              <div className="mt-1 text-xs text-neutral-500">Отображается в блоке справа по центру экрана часов. Оставьте пустым, чтобы скрыть блок.</div>
            </div>

            <div>
              <Label>Фон часов на ТВ</Label>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" />
              {backgroundImage ? (
                <div className="mt-2 overflow-hidden rounded-lg border border-neutral-200">
                  <div className="relative h-32 w-full bg-cover bg-center" style={{ backgroundImage: `url(${backgroundImage})` }}>
                    <div className="absolute inset-0 bg-black/30" />
                    <button
                      type="button"
                      onClick={() => setBackgroundImage('')}
                      className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                      title="Удалить фон"
                    ><X className="h-4 w-4" /></button>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full items-center justify-center gap-2 border-t border-neutral-200 bg-neutral-50 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                  ><Upload className="h-4 w-4" /> Заменить</button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 py-6 text-sm text-neutral-600 hover:border-red-400 hover:bg-red-50 hover:text-red-600"
                >
                  <ImageIcon className="h-6 w-6" />
                  <span>Загрузить изображение</span>
                  <span className="text-xs text-neutral-400">JPG / PNG / WEBP до 4 МБ</span>
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-red-600">2 · Структура блайндов</div>
                <div className="text-sm text-neutral-600">Перетаскивайте строки за <span className="inline-flex items-center gap-1"><GripVertical className="h-3 w-3" /></span>, чтобы поменять порядок. Номера обновятся автоматически.</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={addBreak} type="button"><Coffee className="mr-1 h-4 w-4" /> Перерыв</Button>
                <Button size="sm" onClick={addBlind} type="button" className="bg-red-600 text-white hover:bg-red-700"><Plus className="mr-1 h-4 w-4" /> Уровень</Button>
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 text-left text-xs uppercase text-neutral-500">
                      <th className="py-2 pr-2 w-8"></th>
                      <th className="py-2 pr-2 w-20">№</th>
                      <th className="py-2 pr-2">SB</th>
                      <th className="py-2 pr-2">BB</th>
                      <th className="py-2 pr-2">Анте</th>
                      <th className="py-2 pr-2">Мин</th>
                      <th className="py-2 pr-2"></th>
                    </tr>
                  </thead>
                  <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                    <tbody>
                      {blinds.map((b, i) => (
                        <SortableRow
                          key={rowIds[i]}
                          id={rowIds[i]}
                          index={i}
                          b={b}
                          updateBlind={updateBlind}
                          removeBlind={removeBlind}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>

            {/* Звуки */}
            <div className="mt-8">
              <div className="text-xs font-semibold uppercase tracking-widest text-red-600">3 · Звуковые оповещения</div>
              <div className="mt-1 text-sm text-neutral-600">Загрузите до двух звуков и укажите, за сколько секунд до конца уровня они должны сыграть.</div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {sounds.map((s, i) => (
                  <SoundSlotCard key={i} slot={s} index={i} onChange={onSoundChange(i)} />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
